package fr.frux.feature.feedback

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.CheckCircle
import androidx.compose.material3.FilterChip
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import fr.frux.core.common.error.AppError
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxTextField
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.components.SnackbarMessageEffect
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.interaction.clearFocusOnTap
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.feedback.resources.Res
import fr.frux.feature.feedback.resources.feedback_category_bug
import fr.frux.feature.feedback.resources.feedback_category_idea
import fr.frux.feature.feedback.resources.feedback_category_other
import fr.frux.feature.feedback.resources.feedback_context_notice
import fr.frux.feature.feedback.resources.feedback_counter
import fr.frux.feature.feedback.resources.feedback_error_limit
import fr.frux.feature.feedback.resources.feedback_intro
import fr.frux.feature.feedback.resources.feedback_message_label
import fr.frux.feature.feedback.resources.feedback_message_placeholder
import fr.frux.feature.feedback.resources.feedback_section_category
import fr.frux.feature.feedback.resources.feedback_sent_back
import fr.frux.feature.feedback.resources.feedback_sent_text
import fr.frux.feature.feedback.resources.feedback_sent_title
import fr.frux.feature.feedback.resources.feedback_submit
import fr.frux.feature.feedback.resources.feedback_title
import fr.frux.shared.content.FeedbackLimits
import fr.frux.shared.domain.model.FeedbackCategory
import org.jetbrains.compose.resources.stringResource

/**
 * Écran « Donner mon avis » (#437, ADR 0028), poussé depuis le Profil. Après l'envoi, la
 * confirmation remplace le formulaire et propose le retour : pas de snackbar à faire survivre à la
 * navigation. Les erreurs passent par [SnackbarMessageEffect] (#438, libellé chargé avant affichage).
 */
@Composable
fun FeedbackScreen(
    sourceScreen: String,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: FeedbackViewModel = rememberFeedbackViewModel(sourceScreen),
) {
    val state by viewModel.state.collectAsState()
    val snackbarHostState = remember { SnackbarHostState() }

    SnackbarMessageEffect(
        trigger = state.error,
        message = state.error?.feedbackMessage(),
        snackbarHostState = snackbarHostState,
    ) { viewModel.consumeError() }

    Scaffold(
        modifier = modifier.fillMaxSize().clearFocusOnTap(),
        topBar = { FruxTopBar(title = stringResource(Res.string.feedback_title), onBack = onBack) },
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = MaterialTheme.colorScheme.background,
    ) { paddingValues ->
        if (state.sent) {
            SentConfirmation(onBack = onBack, modifier = Modifier.padding(paddingValues))
        } else {
            FeedbackForm(state = state, viewModel = viewModel, modifier = Modifier.padding(paddingValues))
        }
    }
}

@Composable
private fun FeedbackForm(
    state: FeedbackState,
    viewModel: FeedbackViewModel,
    modifier: Modifier,
) {
    Column(
        modifier =
            modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
        verticalArrangement = Arrangement.spacedBy(FruxSpacing.md),
    ) {
        Text(text = stringResource(Res.string.feedback_intro), style = MaterialTheme.typography.bodyLarge)
        Text(text = stringResource(Res.string.feedback_section_category), style = MaterialTheme.typography.titleSmall)
        Row(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
            FeedbackCategory.entries.forEach { category ->
                FilterChip(
                    selected = state.category == category,
                    onClick = { viewModel.onCategorySelected(category) },
                    label = { Text(category.label()) },
                )
            }
        }
        FruxTextField(
            value = state.message,
            onValueChange = viewModel::onMessageChange,
            label = stringResource(Res.string.feedback_message_label),
            placeholder = stringResource(Res.string.feedback_message_placeholder),
            singleLine = false,
            maxLength = FeedbackLimits.MESSAGE_MAX,
            supportingText =
                stringResource(
                    Res.string.feedback_counter,
                    state.message.codePointLength(),
                    FeedbackLimits.MESSAGE_MAX,
                ),
            modifier = Modifier.fillMaxWidth(),
        )
        Text(
            text = stringResource(Res.string.feedback_context_notice),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        FruxButton(
            text = stringResource(Res.string.feedback_submit),
            onClick = viewModel::send,
            enabled = state.canSend,
            loading = state.isSending,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

@Composable
private fun SentConfirmation(
    onBack: () -> Unit,
    modifier: Modifier,
) {
    Column(
        modifier = modifier.fillMaxSize().padding(FruxSpacing.lg),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(FruxSpacing.md, Alignment.CenterVertically),
    ) {
        Icon(
            imageVector = Icons.Outlined.CheckCircle,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.primary,
            modifier = Modifier.size(48.dp),
        )
        Text(text = stringResource(Res.string.feedback_sent_title), style = MaterialTheme.typography.titleLarge)
        Text(
            text = stringResource(Res.string.feedback_sent_text),
            style = MaterialTheme.typography.bodyLarge,
            textAlign = TextAlign.Center,
        )
        FruxButton(text = stringResource(Res.string.feedback_sent_back), onClick = onBack)
    }
}

@Composable
private fun FeedbackCategory.label(): String =
    when (this) {
        FeedbackCategory.BUG -> stringResource(Res.string.feedback_category_bug)
        FeedbackCategory.IDEA -> stringResource(Res.string.feedback_category_idea)
        FeedbackCategory.OTHER -> stringResource(Res.string.feedback_category_other)
    }

/** La limite par compte a son propre libellé, le reste suit la taxonomie commune (#176). */
@Composable
private fun AppError.feedbackMessage(): String =
    if (this is AppError.RateLimited && quotaExhausted) {
        stringResource(Res.string.feedback_error_limit)
    } else {
        userMessage()
    }
